export const MESSENGER_REACTION_TYPES = [
  '\u{1F44D}',
  '\u{2764}\u{FE0F}',
  '\u{1F602}',
  '\u{1F62E}',
  '\u{1F622}',
  '\u{1F621}',
];
const REACTION_TYPES_SET = new Set(MESSENGER_REACTION_TYPES);
export const LOCAL_REACTION_HOLD_MS = 15000;

export const parseBooleanish = (value) => {
  if (value == null || `${value}` === '') return null;
  const normalized = `${value}`.toLowerCase();
  if (value === true || value === 1 || normalized === 'true' || normalized === '1') return true;
  if (value === false || value === 0 || normalized === 'false' || normalized === '0') return false;
  return null;
};

export const toFiniteId = (value) => {
  if (value == null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const resolveReactionType = (value) => {
  const normalized = `${value ?? ''}`.trim();
  return REACTION_TYPES_SET.has(normalized) ? normalized : null;
};

export const resolveReactionUserId = (reaction) =>
  toFiniteId(
    reaction?.user_id ??
      reaction?.userId ??
      reaction?.reactor_id ??
      reaction?.reactorId ??
      reaction?.participant_id ??
      reaction?.participantId,
  );

const resolveReactionArrayEntryUserId = (entry) => {
  if (entry && typeof entry === 'object') {
    return resolveReactionUserId(entry);
  }
  return toFiniteId(entry);
};

export const resolveReactionFromResponse = (response) =>
  response?.response?.reaction ?? response?.reaction ?? null;

export const resolveReactionPayloadFromMessageResponse = (response) => {
  const envelope = response?.response ?? response ?? null;
  if (!envelope || typeof envelope !== 'object') return null;

  // Supported backend contract:
  // 1) { messageId, reactions: { "❤️": [{ userId, createdAt }] }, total }
  // 2) { status, data: { messageId, reactions: { ... }, total } }
  const source = envelope?.reactions ?? envelope?.data?.reactions ?? null;
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null;
  return { reactions: source };
};

export const resolveReactionMutationFromResponse = (response) => {
  const raw =
    response?.response?.message ??
    response?.message ??
    response?.response?.status ??
    response?.status ??
    '';
  const normalized = `${raw}`.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('delete')) return 'removed';
  if (normalized.includes('remove')) return 'removed';
  if (normalized.includes('add')) return 'added';
  return null;
};

export const buildReactionSummary = (
  message,
  { currentUserId = null, parseOwnFlag = () => null } = {},
) => {
  const entries = resolveMessageReactionEntries(message, { currentUserId, parseOwnFlag });
  return entries.reduce((summary, entry) => {
    if (!entry?.type) return summary;
    summary[entry.type] = {
      count: Math.max(0, Number(entry?.count) || 0),
      reacted_by_current_user: entry?.reactedByCurrentUser === true,
    };
    return summary;
  }, {});
};

const upsertReactionSummary = (summary, type, countDelta = 0, reactedByCurrentUser = false) => {
  if (!type) return;
  const current = summary.get(type) || {
    type,
    count: 0,
    reactedByCurrentUser: false,
    reactorIds: new Set(),
  };
  if (!(current.reactorIds instanceof Set)) {
    current.reactorIds = new Set(
      Array.isArray(current.reactorIds) ? current.reactorIds.map((id) => `${id}`) : [],
    );
  }
  current.count += Math.max(0, Number(countDelta) || 0);
  current.reactedByCurrentUser = current.reactedByCurrentUser || reactedByCurrentUser === true;
  summary.set(type, current);
};

export const resolveMessageReactionEntries = (
  message,
  { currentUserId = null, parseOwnFlag = parseBooleanish } = {},
) => {
  const summary = new Map();
  const normalizedCurrentUserId = toFiniteId(currentUserId);
  const source =
    message?.reactions ??
    message?.reaction_summary ??
    message?.reactionSummary ??
    message?.metadata?.reactions ??
    message?.metadata?.reaction_summary ??
    null;

  if (Array.isArray(source)) {
    source.forEach((entry) => {
      if (typeof entry === 'string') {
        const type = resolveReactionType(entry);
        if (type) upsertReactionSummary(summary, type, 1, false);
        return;
      }
      if (!entry || typeof entry !== 'object') return;

      const type = resolveReactionType(
        entry?.reactionType ??
          entry?.reaction_type ??
          entry?.type ??
          entry?.emoji ??
          entry?.reaction,
      );
      if (!type) return;

      const explicitCount = Number(entry?.count ?? entry?.total ?? entry?.value ?? Number.NaN);
      const userId = resolveReactionUserId(entry);
      const ownFlag =
        parseOwnFlag(entry?.reacted_by_current_user) ??
        parseOwnFlag(entry?.isCurrentUser) ??
        parseOwnFlag(entry?.is_own) ??
        parseOwnFlag(entry?.is_self);
      const reactedByCurrentUser =
        ownFlag === true ||
        (userId != null &&
          normalizedCurrentUserId != null &&
          `${userId}` === `${normalizedCurrentUserId}`);

      if (
        Number.isFinite(explicitCount) &&
        explicitCount > 0 &&
        userId == null &&
        ownFlag == null
      ) {
        upsertReactionSummary(summary, type, explicitCount, reactedByCurrentUser);
      } else {
        upsertReactionSummary(summary, type, 1, reactedByCurrentUser);
        if (userId != null) {
          summary.get(type)?.reactorIds?.add(`${userId}`);
        }
      }
    });
  } else if (source && typeof source === 'object') {
    Object.entries(source).forEach(([key, rawValue]) => {
      const type = resolveReactionType(key);
      if (!type) return;

      if (Array.isArray(rawValue)) {
        const knownUserIds = rawValue
          .map((entry) => resolveReactionArrayEntryUserId(entry))
          .filter((entry) => entry != null);
        const reactedByCurrentUser =
          normalizedCurrentUserId != null &&
          knownUserIds.some((entry) => `${entry}` === `${normalizedCurrentUserId}`);
        upsertReactionSummary(summary, type, rawValue.length, reactedByCurrentUser);
        if (knownUserIds.length > 0) {
          const entrySummary = summary.get(type);
          knownUserIds.forEach((entry) => entrySummary?.reactorIds?.add(`${entry}`));
        }
        return;
      }

      if (rawValue && typeof rawValue === 'object') {
        const nestedCount = Number(
          rawValue?.count ?? rawValue?.total ?? rawValue?.value ?? Number.NaN,
        );
        const nestedOwn =
          parseOwnFlag(rawValue?.reacted_by_current_user) ??
          parseOwnFlag(rawValue?.isCurrentUser) ??
          parseOwnFlag(rawValue?.is_own) ??
          parseOwnFlag(rawValue?.is_self) ??
          false;
        if (Number.isFinite(nestedCount) && nestedCount > 0) {
          upsertReactionSummary(summary, type, nestedCount, nestedOwn === true);
          return;
        }
      }

      const numericCount = Number(rawValue);
      if (Number.isFinite(numericCount) && numericCount > 0) {
        upsertReactionSummary(summary, type, numericCount, false);
      }
    });
  }

  return MESSENGER_REACTION_TYPES.map((type) => summary.get(type))
    .filter((entry) => entry && entry.count > 0)
    .map((entry) => ({
      type: entry.type,
      count: entry.count,
      reactedByCurrentUser: entry.reactedByCurrentUser === true,
      reactorIds: Array.from(entry.reactorIds || [])
        .map((id) => toFiniteId(id))
        .filter((id) => id != null),
      hasUnknownReactors: Number(entry.count) > (entry?.reactorIds?.size ?? 0),
    }));
};

export const buildReactionTooltip = (reaction) => {
  const names = Array.isArray(reaction?.reactorNames) ? reaction.reactorNames.filter(Boolean) : [];
  if (!names.length) {
    const count = Number(reaction?.count) || 0;
    return count === 1 ? '1 reaction' : `${count} reactions`;
  }

  const knownNames = [...new Set(names)];
  const count = Number(reaction?.count) || knownNames.length;
  const extra = Math.max(0, count - knownNames.length);
  return extra > 0 ? `${knownNames.join(', ')}, +${extra} more` : knownNames.join(', ');
};

export const withOptimisticReaction = (
  message,
  { reactionType, reactorUserId = null, currentUserId = null, parseOwnFlag = () => null } = {},
) => {
  if (!message || typeof message !== 'object') return message;
  const type = resolveReactionType(reactionType);
  if (!type) return message;
  const resolvedReactorUserId = toFiniteId(reactorUserId ?? currentUserId);
  const isOwnReactionEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const entryUserId = resolveReactionUserId(entry);
    if (
      resolvedReactorUserId != null &&
      entryUserId != null &&
      `${entryUserId}` === `${resolvedReactorUserId}`
    ) {
      return true;
    }
    const ownFlag =
      parseOwnFlag(entry?.reacted_by_current_user) ??
      parseOwnFlag(entry?.isCurrentUser) ??
      parseOwnFlag(entry?.is_own) ??
      parseOwnFlag(entry?.is_self);
    return ownFlag === true;
  };

  const summary = buildReactionSummary(message, { currentUserId, parseOwnFlag });
  // Enforce one reaction per user: remove any existing own reaction from other emoji types first.
  Object.keys(summary).forEach((summaryType) => {
    if (summaryType === type) return;
    const entry = summary[summaryType];
    if (!entry || entry?.reacted_by_current_user !== true) return;
    const decremented = Math.max(0, (Number(entry?.count) || 0) - 1);
    if (decremented <= 0) {
      delete summary[summaryType];
      return;
    }
    summary[summaryType] = {
      ...entry,
      count: decremented,
      reacted_by_current_user: false,
    };
  });

  const current = summary[type] || { count: 0, reacted_by_current_user: false };
  const nextCount =
    current.reacted_by_current_user === true
      ? Math.max(1, Number(current.count) || 1)
      : (Number(current.count) || 0) + 1;
  summary[type] = {
    count: nextCount,
    reacted_by_current_user: true,
  };

  const existingReactions = Array.isArray(message?.reactions) ? message.reactions : [];
  const reactionsWithoutOtherOwnTypes = existingReactions.filter((entry) => {
    if (!entry || typeof entry !== 'object') return true;
    const entryType = resolveReactionType(
      entry?.reaction_type ?? entry?.reactionType ?? entry?.type ?? entry?.emoji ?? entry?.reaction,
    );
    if (!entryType || entryType === type) return true;
    return !isOwnReactionEntry(entry);
  });

  const hasExistingOwnEntry = reactionsWithoutOtherOwnTypes.some((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const entryType = resolveReactionType(
      entry?.reaction_type ?? entry?.reactionType ?? entry?.type ?? entry?.emoji ?? entry?.reaction,
    );
    return entryType === type && isOwnReactionEntry(entry);
  });

  const reactions =
    hasExistingOwnEntry || resolvedReactorUserId == null
      ? reactionsWithoutOtherOwnTypes
      : [
          ...reactionsWithoutOtherOwnTypes,
          {
            reaction_type: type,
            user_id: resolvedReactorUserId,
            reacted_by_current_user:
              currentUserId != null && `${resolvedReactorUserId}` === `${currentUserId}`,
          },
        ];

  return {
    ...message,
    ...(Array.isArray(reactions) ? { reactions } : {}),
    reaction_summary: summary,
    reactionSummary: summary,
    metadata: {
      ...(message?.metadata && typeof message.metadata === 'object' ? message.metadata : {}),
      reaction_summary: summary,
      __localReactionUpdatedAt: Date.now(),
      __localReactionMutation: 'added',
    },
  };
};

export const withOptimisticReactionRemoved = (
  message,
  { reactionType, reactorUserId = null, currentUserId = null, parseOwnFlag = () => null } = {},
) => {
  if (!message || typeof message !== 'object') return message;
  const type = resolveReactionType(reactionType);
  if (!type) return message;
  const resolvedReactorUserId = toFiniteId(reactorUserId ?? currentUserId);

  const summary = buildReactionSummary(message, { currentUserId, parseOwnFlag });
  const current = summary[type];
  if (!current) return message;

  const currentCount = Math.max(0, Number(current?.count) || 0);
  const nextCount = Math.max(0, currentCount - 1);
  if (nextCount <= 0) {
    delete summary[type];
  } else {
    summary[type] = {
      ...current,
      count: nextCount,
      reacted_by_current_user: false,
    };
  }

  const existingReactions = Array.isArray(message?.reactions) ? message.reactions : [];
  let removedOne = false;
  const reactions = existingReactions.filter((entry) => {
    if (!entry || typeof entry !== 'object') return true;
    const entryType = resolveReactionType(
      entry?.reaction_type ?? entry?.reactionType ?? entry?.type ?? entry?.emoji ?? entry?.reaction,
    );
    if (entryType !== type) return true;

    const entryUserId = resolveReactionUserId(entry);
    if (
      !removedOne &&
      resolvedReactorUserId != null &&
      entryUserId != null &&
      `${entryUserId}` === `${resolvedReactorUserId}`
    ) {
      removedOne = true;
      return false;
    }

    const entryOwn =
      parseOwnFlag(entry?.reacted_by_current_user) ??
      parseOwnFlag(entry?.isCurrentUser) ??
      parseOwnFlag(entry?.is_own) ??
      parseOwnFlag(entry?.is_self);
    if (!removedOne && entryOwn === true) {
      removedOne = true;
      return false;
    }

    return true;
  });

  return {
    ...message,
    ...(Array.isArray(existingReactions) ? { reactions } : {}),
    reaction_summary: summary,
    reactionSummary: summary,
    metadata: {
      ...(message?.metadata && typeof message.metadata === 'object' ? message.metadata : {}),
      reaction_summary: summary,
      __localReactionUpdatedAt: Date.now(),
      __localReactionMutation: 'removed',
    },
  };
};

export const extractReactionPayload = (message) => {
  if (!message || typeof message !== 'object') return null;

  const payload = {};
  if (message?.reactions != null) payload.reactions = message.reactions;
  if (message?.reaction_summary != null) payload.reaction_summary = message.reaction_summary;
  if (message?.reactionSummary != null) payload.reactionSummary = message.reactionSummary;

  const metadata = message?.metadata;
  if (metadata && typeof metadata === 'object') {
    const metadataPayload = {};
    if (metadata?.reactions != null) metadataPayload.reactions = metadata.reactions;
    if (metadata?.reaction_summary != null)
      metadataPayload.reaction_summary = metadata.reaction_summary;
    if (metadata?.reactionSummary != null)
      metadataPayload.reactionSummary = metadata.reactionSummary;
    const localReactionUpdatedAt = Number(metadata?.__localReactionUpdatedAt);
    if (Number.isFinite(localReactionUpdatedAt)) {
      metadataPayload.__localReactionUpdatedAt = localReactionUpdatedAt;
    }
    if (typeof metadata?.__localReactionMutation === 'string') {
      metadataPayload.__localReactionMutation = metadata.__localReactionMutation;
    }
    if (Object.keys(metadataPayload).length > 0) {
      payload.metadata = metadataPayload;
    }
  }

  return Object.keys(payload).length > 0 ? payload : null;
};

export const applyReactionPayload = (message, reactionPayload) => {
  if (!message || typeof message !== 'object' || !reactionPayload) return message;

  return {
    ...message,
    ...(reactionPayload?.reactions != null ? { reactions: reactionPayload.reactions } : {}),
    ...(reactionPayload?.reaction_summary != null
      ? { reaction_summary: reactionPayload.reaction_summary }
      : {}),
    ...(reactionPayload?.reactionSummary != null
      ? { reactionSummary: reactionPayload.reactionSummary }
      : {}),
    ...(reactionPayload?.metadata
      ? {
          metadata: {
            ...(message?.metadata && typeof message.metadata === 'object' ? message.metadata : {}),
            ...reactionPayload.metadata,
          },
        }
      : {}),
  };
};
